import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Grid,
  Dialog,
  DialogContent,
  CircularProgress,
  Typography,
  Box,
  useTheme,
} from '@mui/material';

import GenericItemNameInput from './GenericItemNameInput';
import UnitOfMeasureSelector from './UnitOfMeasureSelector';
import AttributeValuesCheckboxes, { SelectedAttributesMap } from './AttributeValuesCheckboxes';
import AttributeGroupSelector from './AttributeGroupSelector';

import { useStore } from 'zustand';
import {
  AllowedVariable,
  AttributeValue,
  AttributeVariable,
  GlobalItem,
  ItemCost,
} from '../../../api/protosCompiled/globalItem/globalItem_pb';
import { useFetchAttributeGroups } from '../../../hooks/useFetchAttributeGroups';
import { useGetMasterFormatDivision } from '../../../hooks/useGetMasterFormatDivision';
import { useGlobalItemStore } from '../../../stores/globalItemStore';
import { useFolderStore } from '../../../stores/folderStore';
import { useCreateOrUpdateItem } from '../../../hooks/useFetchItemsByFileId';
import { getContrastTextColor } from '../../../utils/getCintrastTextColor';
import { useQueryClient } from 'react-query';
import React from 'react';

export type CreateItemProps = {
  items: GlobalItem.AsObject[];
};

const CreateItem = ({ items }: CreateItemProps) => {
  const theme = useTheme();

  //zustand states
  const { currentItem, isCreatingItem } = useStore(useGlobalItemStore);
  const { currentFolder } = useStore(useFolderStore);

  //react-query queries
  const { data: masterFormatDivision } = useGetMasterFormatDivision();
  const { data: attributeGroups } = useFetchAttributeGroups();
  const { mutate: createOrUpdateItem } = useCreateOrUpdateItem();

  // Initialize query client
  const queryClient = useQueryClient();

  //local state
  const [itemName, setItemName] = useState(currentItem?.itemname || '');
  const [selectedUom, setSelectedUom] = useState<string | null>(null);
  const [selectedAttributeGroupId, setSelectedAttributeGroupId] = useState<string | null>(null);
  const [selectedAttributes, setSelectedAttributes] = useState<SelectedAttributesMap>({});
  const [filterDivision, setFilterDivision] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Memoize derived values
  const backgroundColor = useMemo(
    () => theme.palette.background.paper,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme.palette.mode, theme.palette.background.default]
  );

  const textColor = useMemo(
    () => getContrastTextColor(theme.palette.background.paper),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme.palette.mode, theme.palette.background.paper]
  );

  // Memoize the attributesList computation
  const attributesList = useMemo(
    () =>
      Object.keys(selectedAttributes).flatMap(groupId =>
        selectedAttributes[groupId].map((valueId: string) => {
          const originalAttribute = items
            .flatMap(item => item.attributesList || [])
            .find(
              attr =>
                attr.attributegroupid.toString() === groupId &&
                attr.attributevalueid.toString() === valueId
            );

          const attribute = new AttributeValue();
          attribute.setAttributevalueid(Number(valueId));
          attribute.setAttributegroupid(Number(groupId));
          attribute.setAttributevaluename(originalAttribute?.attributevaluename || '');
          attribute.setUpc(originalAttribute?.upc || '');
          attribute.setItemattributevaluemapid(originalAttribute?.itemattributevaluemapid || 0);

          attribute.setAttributevariablesList(
            (originalAttribute?.attributevariablesList || []).map(variable => {
              const attrVar = new AttributeVariable();
              attrVar.setItemattributevaluevariablemapid(
                variable.itemattributevaluevariablemapid || 0
              );
              attrVar.setVariableid(variable.variableid || 0);
              if (variable.variable) {
                const allowedVariable = new AllowedVariable();
                attrVar.setVariable(allowedVariable);
              }
              attrVar.setVariablevalue(variable.variablevalue || 0);
              return attrVar;
            })
          );
          return attribute;
        })
      ),
    [selectedAttributes, items]
  );

  // Memoize the costsList computation
  const costsList = useMemo(
    () =>
      Object.keys(selectedAttributes).flatMap(groupId =>
        selectedAttributes[groupId].map((valueId: string) => {
          const existingCost = currentItem?.costsList?.find(cost => {
            return String(cost.itemattributevalueid) === valueId;
          });
          const cost = new ItemCost();
          if (existingCost) {
            cost.setItemid(existingCost.itemid);
            cost.setItemcostid(existingCost.itemcostid);
            cost.setItemunitcost(existingCost.itemunitcost);
            cost.setHourlyproductionrate(existingCost.hourlyproductionrate);
          } else {
            cost.setItemid('');
            cost.setItemcostid(0);
            cost.setItemunitcost(0);
            cost.setHourlyproductionrate(0);
          }
          cost.setItemattributevalueid(Number(valueId));
          cost.setUomid(Number(selectedUom));
          return cost;
        })
      ),
    [selectedAttributes, currentItem?.costsList, selectedUom]
  );

  //effect to set the form to the current item
  useEffect(() => {
    if (!currentItem) return;
    setItemName(currentItem?.itemname || ''); // Update item name
    setSelectedUom(currentItem?.costsList[0]?.uomid?.toString() || ''); // Update UOM
    setSelectedAttributeGroupId(currentItem?.attributesList[0]?.attributegroupid?.toString() || '');

    // Ensure selectedAttributes is always an object with valid arrays
    setSelectedAttributes(
      currentItem.attributesList.reduce(
        (acc, attr) => {
          const groupId = String(attr.attributegroupid);
          if (!acc[groupId]) acc[groupId] = [];
          acc[groupId].push(String(attr.attributevalueid));
          return acc;
        },
        {} as Record<string, string[]>
      )
    );
  }, [currentItem]);

  // Memoize the handleCreateItem callback
  const handleCreateItem = useCallback(async () => {
    try {
      setIsSubmitting(true);
      const newItem = new GlobalItem();

      const isUpdate = !isCreatingItem;
      const itemId = isCreatingItem ? '' : currentItem?.itemid || '';
      const parentId = isCreatingItem ? currentFolder!.folderid : currentItem!.parentid || '';

      newItem.setItemid(itemId);
      newItem.setItemname(itemName);
      newItem.setIsactive(true);
      newItem.setLastupdated('');
      newItem.setParentid(parentId);
      newItem.setLevel(isCreatingItem ? currentFolder!.level + 1 : currentItem?.level || 2);
      newItem.setEntitytype('item');
      newItem.setIsfile(true);

      newItem.setAttributesList(attributesList);
      newItem.setCostsList(costsList);

      await createOrUpdateItem(newItem, {
        onSuccess: () => {
          // More aggressive cache invalidation to ensure UI updates
          queryClient.invalidateQueries(['itemsByFileId', parentId]);
          if (isUpdate && currentItem?.parentid && currentItem.parentid !== parentId) {
            queryClient.invalidateQueries(['itemsByFileId', currentItem.parentid]);
          }
          // Invalidate all item-related queries to ensure complete refresh
          queryClient.invalidateQueries('itemsByFileId');
          queryClient.invalidateQueries('itemFolders');

          // Force refetch the specific folder's items
          queryClient.refetchQueries(['itemsByFileId', parentId], {
            exact: true,
          });

          setIsSubmitting(false);
        },
        onError: (error: any) => {
          console.error('Error creating/updating item:', error);
          setIsSubmitting(false);
        },
      });
    } catch (error) {
      console.error(error);
      setIsSubmitting(false);
    }
  }, [
    isCreatingItem,
    currentItem,
    currentFolder,
    itemName,
    attributesList,
    costsList,
    createOrUpdateItem,
    queryClient,
  ]);

  // Memoize the handleCheckboxChange callback
  const handleCheckboxChange = useCallback((groupId: string, valueId: string) => {
    setSelectedAttributes((prevSelected: any) => {
      const groupValues = prevSelected[groupId] || [];
      const updatedValues = groupValues.includes(valueId)
        ? groupValues.filter((id: string) => id !== valueId)
        : [...groupValues, valueId];

      return {
        ...prevSelected,
        [groupId]: updatedValues,
      };
    });
  }, []);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: backgroundColor,
        color: textColor,
      }}
    >
      <Grid
        container
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Grid
          item
          xs={12}
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <GenericItemNameInput
                value={itemName}
                onChange={setItemName}
                required={true}
                error={!itemName}
                helperText={!itemName ? 'Item name is required' : ''}
              />
              <UnitOfMeasureSelector
                value={selectedUom}
                onChange={setSelectedUom}
                required={true}
                error={!selectedUom}
                helperText={!selectedUom ? 'Unit of measure is required' : ''}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <AttributeGroupSelector
                filterDivision={filterDivision}
                setFilterDivision={setFilterDivision}
                selectedAttributeGroupId={selectedAttributeGroupId}
                setSelectedAttributeGroupId={setSelectedAttributeGroupId}
                attributeGroups={attributeGroups}
                masterFormatDivision={masterFormatDivision || []}
                required={true}
                error={!selectedAttributeGroupId}
                helperText={!selectedAttributeGroupId ? 'Attribute group is required' : ''}
              />
            </Grid>
          </Grid>

          <Box sx={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
            <AttributeValuesCheckboxes
              selectedAttributeGroupId={selectedAttributeGroupId}
              selectedAttributes={selectedAttributes}
              setSelectedAttributes={setSelectedAttributes}
              attributeGroups={attributeGroups}
              handleCheckboxChange={handleCheckboxChange}
              itemName={itemName}
              setItemName={setItemName}
              selectedUom={selectedUom || ''}
              setSelectedUom={setSelectedUom}
              handleCreateItem={handleCreateItem}
              userBackgroundColor={theme.palette.background.paper}
            />
          </Box>
        </Grid>
      </Grid>

      <Dialog
        open={isSubmitting}
        disableEscapeKeyDown
        aria-labelledby="loading-dialog-title"
        aria-describedby="loading-dialog-description"
        PaperProps={{
          sx: {
            backgroundColor:
              theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
            color:
              theme.palette.mode === 'dark'
                ? theme.palette.text.primary
                : theme.palette.background.paper
                  ? getContrastTextColor(theme.palette.background.paper)
                  : theme.palette.text.primary,
            boxShadow: theme.shadows[5],
            padding: '20px',
          },
        }}
      >
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <CircularProgress
              size={50}
              sx={{
                color:
                  theme.palette.mode === 'dark'
                    ? theme.palette.primary.light
                    : theme.palette.background.paper
                      ? getContrastTextColor(theme.palette.background.paper)
                      : theme.palette.primary.main,
              }}
            />
            <Typography id="loading-dialog-title" variant="h6" sx={{ color: 'inherit' }}>
              Please wait while we create your items...
            </Typography>
            <Typography
              id="loading-dialog-description"
              sx={{ color: theme.palette.text.secondary }}
            >
              This may take a few moments
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default React.memo(CreateItem);
